import LibGenerateTestUserSig from '@msb/trtcsign' // 请移除，客户端计算token并不安全
import EventEmitter from 'events'
import { toast } from 'react-hot-toast'
import TRTC from 'trtc-sdk-v5'

import Rtc, { ActMediaType, ChangeCamera, ChangeMicrophone, Correct, CorrectType, MediaType, PlayConf } from '.'
import FabricTrack from './fabricTrack'

interface IAgoraNetworkQuality {
  uplinkNetworkQuality: number
  downlinkNetworkQuality: number
}

/**
 * 采集分辨率，可以提为入参
 * 高度不够，上下留黑
 * 高度正常，宽度超出，左右截取
 * 宽度不够，宽缩放至100%，高等比缩放，上下留黑，高度不会裁剪
 */
const videSize = {
  width: 1280, // 4:3
  height: 960
}
// https://web.sdk.qcloud.com/trtc/webrtc/v5/doc/zh-cn/global.html#VideoProfile
const cameraVideoProfile = {
  with: videSize.width,
  height: videSize.height,
  frameRate: 15,
  bitrate: 1500
}

const defaultVideoOptions = {
  profile: cameraVideoProfile,
  qosPreference: TRTC.TYPE.QOS_PREFERENCE_CLEAR,
  fillMode: 'contain',
  mirror: true
}

export interface RtcOptions {
  channel: string
  uid: number
  token?: string
  viceToken?: string
}

function calNetworkQuality(rtt: number, loss: number) {
  if (loss > 50 || rtt > 500) {
    return 5
  } else if (loss > 30 || rtt > 350) {
    return 4
  } else if (loss > 20 || rtt > 200) {
    return 3
  } else if (loss > 10 || rtt > 100) {
    return 2
  } else if (loss >= 0 || rtt >= 0) {
    return 1
  }
  return 0
}

const generator = new LibGenerateTestUserSig(Number(import.meta.env.VITE_APP_TRTC_APPID), import.meta.env.VITE_APP_TRTC_SECRET, 1 * 24 * 60 * 60)

export default class Trtc extends EventEmitter {
  private appId: number = Number(import.meta.env.VITE_APP_TRTC_APPID || 0)
  private channel: string = ''
  private token: string | null = null
  private viceToken: string | null = null
  private uid: number = 0
  private mainUid: string = ''
  private viceUid: string = ''
  public mainClient!: TRTC
  public viceClient: TRTC | null = null
  public mainFabricTrack: FabricTrack | null = null // fabricTrack 自定义摄像头
  public viceFabricTrack: FabricTrack | null = null // fabricTrack 自定义摄像头
  public localMainVideoTrack: MediaStreamTrack | null = null
  private mainVideoView?: string
  private viceVideoView?: string
  public localViceVideoTrack: MediaStreamTrack | null = null
  public localAudioTrack: MediaStreamTrack | null = null
  public remoteVideoTrack: { uid: string; videoTrack: MediaStreamTrack | null }[] = []
  public remoteAudioTrack: { uid: string; videoTrack: MediaStreamTrack | null }[] = []

  // 设备ID
  public mainCameraDeviceId: string = ''
  public viceCameraDeviceId: string = ''
  public microphoneDeviceId: string = ''

  constructor(appId: string) {
    super()
    this.appId = Number(appId)
  }

  public async init(options: RtcOptions) {
    const { channel, token, viceToken, uid } = options
    this.channel = channel
    this.uid = uid

    this.mainUid = `20${this.uid}`
    this.viceUid = `21${this.uid}`
    this.mainClient = TRTC.create()
    this.viceClient = TRTC.create()
    // this.token = token || null
    // this.viceToken = viceToken || null
    // 请移除，客户端计算token并不安全
    this.token = generator.genTestUserSig(this.mainUid)
    this.viceToken = generator.genTestUserSig(this.viceUid)
    this.subscribeClientEvents()
    await this.join(this.mainUid, this.mainClient, this.token)
    await this.join(this.viceUid, this.viceClient, this.viceToken)
  }

  subscribeClientEvents() {
    // 远端进退房
    this.mainClient.on(TRTC.EVENT.REMOTE_USER_ENTER, ({ userId }) => {
      // console.info('user-joined', userId)
      this.userJoined(Number(userId))
    })
    this.mainClient.on(TRTC.EVENT.REMOTE_USER_EXIT, ({ userId }) => {
      // console.info('user-leave', userId)
      this.userLeft(Number(userId))
    })
    // 远端音视频
    this.mainClient.on(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, async ({ userId, streamType }) => {
      if (userId === this.viceUid) {
        return
      }
      const remoteUser = { uid: userId, videoTrack: this.mainClient.getVideoTrack({ userId, streamType }) }
      console.info('REMOTE_VIDEO_AVAILABLE', remoteUser)
      this.remoteVideoTrack.push(remoteUser)
      // tips: 在收到用户加入才初始化界面的情况下，收流回调需要延迟，避免监听错过
      setTimeout(() => {
        super.emit('user-published', 'video', remoteUser)
      }, 1000)
    })
    this.mainClient.on(TRTC.EVENT.REMOTE_VIDEO_UNAVAILABLE, async ({ userId }) => {
      const index = this.remoteVideoTrack.findIndex((v) => v.uid === userId)
      if (index >= 0) {
        this.remoteVideoTrack.splice(index, 1)
      }
      // tips: 在收到用户加入才初始化界面的情况下，收流回调需要延迟，避免监听错过
      super.emit('user-unpublished', 'video', { uid: userId, videoTrack: this.mainClient.getVideoTrack({ userId }) })
    })
    this.mainClient.on(TRTC.EVENT.REMOTE_AUDIO_AVAILABLE, async ({ userId }) => {
      const remoteUser = { uid: userId, videoTrack: this.mainClient.getAudioTrack({ userId }) }
      console.info('REMOTE_AUDIO_AVAILABLE', remoteUser)
      this.remoteAudioTrack.push(remoteUser)
      setTimeout(() => {
        super.emit('user-published', 'audio', remoteUser)
      }, 1000)
    })
    this.mainClient.on(TRTC.EVENT.REMOTE_AUDIO_UNAVAILABLE, ({ userId }) => {
      const index = this.remoteAudioTrack.findIndex((v) => v.uid === userId)
      if (index >= 0) {
        this.remoteAudioTrack.splice(index, 1)
      }
      super.emit('user-unpublished', 'audio', { uid: userId, videoTrack: this.mainClient.getAudioTrack({ userId }) })
    })
    this.mainClient.on(TRTC.EVENT.AUDIO_VOLUME, ({ result }) => {
      result.forEach(({ userId, volume }) => {
        super.emit('volume-indicator', {
          uid: userId,
          level: volume
        })
      })
    })
    // 网络连接状态
    this.mainClient.on(TRTC.EVENT.CONNECTION_STATE_CHANGED, ({ state, prevState }) => {
      super.emit('connection-state', state, prevState)
    })
    this.mainClient.on(TRTC.EVENT.NETWORK_QUALITY, ({ uplinkNetworkQuality, downlinkNetworkQuality }) => {
      console.info('uplinkNetworkQuality', uplinkNetworkQuality, 'downlinkNetworkQuality', downlinkNetworkQuality)
    })
    // 声网的回调有所有远端用户的统计信息，我们的NETWORK_QUALITY无法兼容， 所以我们自己计算一下
    this.mainClient.on(TRTC.EVENT.STATISTICS, (statistics) => {
      const uplevel = calNetworkQuality(statistics.rtt, statistics.upLoss)
      const downlevel = calNetworkQuality(statistics.rtt, statistics.downLoss)
      const local: IAgoraNetworkQuality = {
        uplinkNetworkQuality: uplevel,
        downlinkNetworkQuality: downlevel
      }

      const remoteNet = {
        uplinkNetworkQuality: downlevel,
        downlinkNetworkQuality: uplevel
      }

      const remote = statistics.remoteStatistics.reduce<Record<string, IAgoraNetworkQuality>>((pre, cur) => {
        pre[cur.userId] = remoteNet
        return pre
      }, {})
      super.emit('network-quality', {
        local,
        remote
      })
    })
  }

  public async join(uid: string, client: TRTC, token: string | null) {
    if (!token) {
      return Promise.reject('token is required')
    }
    try {
      await client.enterRoom({
        strRoomId: this.channel.toString(),
        userId: uid,
        userSig: token,
        sdkAppId: Number(this.appId),
        scene: TRTC.TYPE.SCENE_LIVE,
        role: TRTC.TYPE.ROLE_ANCHOR,
        autoReceiveAudio: uid === this.mainUid
      })
      super.emit('join-success', uid)
    } catch (e) {
      super.emit('join-fail', e)
      return Promise.reject(e)
    }
  }

  public async play(track: any, element: HTMLElement, config?: PlayConf, uid?: number) {
    const isMain = track?.id && track.id === this.localMainVideoTrack?.id
    const isVice = track?.id && track.id === this.localViceVideoTrack?.id
    console.info('play', track, element, config, 'isMain', isMain, 'isVice', isVice, uid)
    const { fit = 'contain', mirror = true } = config || {}
    if (!element) {
      return
    }
    if (isMain) {
      if (element.id === this.viceVideoView) {
        await this.viceClient?.updateLocalVideo({ view: null })
        this.viceVideoView = undefined
      }
      this.mainVideoView = element.id
      this.mainClient?.updateLocalVideo({ view: element, option: { fillMode: fit, mirror } })
    } else if (isVice) {
      if (element.id === this.mainVideoView) {
        await this.mainClient?.updateLocalVideo({ view: null })
        this.mainVideoView = undefined
      }
      this.viceVideoView = element.id
      this.viceClient?.updateLocalVideo({ view: element, option: { fillMode: fit, mirror } })
    } else {
      // 注意， 对端先进房的时候trtc的getVideoTrack 是null，所以没有videoTrack的uid就是远端用户
      const userId = this.remoteVideoTrack.find((v) => v.videoTrack && track && v.videoTrack?.id === track?.id)?.uid
      if (userId || uid) {
        console.info('startRemoteVideo', userId || uid, element, track)
        try {
          this.mainClient?.startRemoteVideo({ view: element, userId: userId || `${uid}`, streamType: TRTC.TYPE.STREAM_TYPE_MAIN, option: { fillMode: fit, mirror } })
        } catch (error) {
          console.log(error)
        }
      }
    }
  }

  userJoined(_uid: number) {
    console.info('Trtc userJoined', _uid)
    super.emit('user-add', _uid.toString())
  }

  userLeft(_uid: number) {
    console.info('Trtc userLeft', _uid)
    super.emit('user-leave', _uid.toString())
  }

  async publishLocalVideoIfNeeded(type: 'mainVideo' | 'viceVideo') {
    const client = type === 'mainVideo' ? this.mainClient : this.viceClient
    const videoTrack = type === 'mainVideo' ? this.localMainVideoTrack : this.localViceVideoTrack
    if (videoTrack) {
      await client?.updateLocalVideo({ publish: true })
    }
  }

  async publishLocalAudioIfNeeded() {
    if (this.localAudioTrack) {
      await this.mainClient?.updateLocalAudio({ publish: true })
      return
    }
    await this.mainClient?.startLocalAudio({ publish: true })
    this.localAudioTrack = this.mainClient!.getAudioTrack()
  }

  public async publish(type: ActMediaType = 'all') {
    console.info('publish', type)
    switch (type) {
      case 'mainVideo':
        await this.publishLocalVideoIfNeeded('mainVideo')
        break
      case 'viceVideo':
        await this.publishLocalVideoIfNeeded('viceVideo')
        break
      case 'audio':
        await this.publishLocalAudioIfNeeded()
        break
      default:
        await this.publishLocalVideoIfNeeded('mainVideo')
        await this.publishLocalVideoIfNeeded('viceVideo')
        await this.publishLocalAudioIfNeeded()
        break
    }
  }

  async changeCamera({ deviceId, publish = true, canvas = false, status = 1 }: ChangeCamera, isMain: boolean) {
    if (canvas) {
      throw new Error('canvas 自定义采集尚未实现')
    }

    const deviceType = isMain ? '主摄像头' : '副摄像头'
    const action = deviceId ? `切换${deviceType}` : `关闭${deviceType}`
    try {
      let localVideoTrack = isMain ? this.localMainVideoTrack : this.localViceVideoTrack
      const localClient = isMain ? this.mainClient : this.viceClient
      const cameraList = await TRTC.getCameraList()
      if (!deviceId || cameraList.findIndex((device) => device.deviceId === deviceId) === -1) {
        localClient?.stopLocalVideo()
        localVideoTrack = null
        if (isMain) {
          this.mainVideoView = undefined
        } else {
          this.viceVideoView = undefined
        }
      } else {
        const params: any = {
          publish,
          mute: false,
          option: { ...defaultVideoOptions, cameraId: deviceId }
        }
        if (localVideoTrack) {
          await localClient?.updateLocalVideo(params)
        } else {
          await localClient?.startLocalVideo(params)
        }
        localVideoTrack = localClient!.getVideoTrack()
      }
      if (isMain) {
        this.localMainVideoTrack = localVideoTrack
      } else {
        this.localViceVideoTrack = localVideoTrack
      }
      super.emit(deviceId ? 'media-ready' : 'media-close', 'video', { uid: isMain ? this.mainUid : this.viceUid, videoTrack: localVideoTrack })
    } catch (error) {
      console.error(error)
      // toast.error(`${action}失败`)
      return Promise.reject(error)
    }
    if (isMain) {
      this.mainCameraDeviceId = deviceId
    } else {
      this.viceCameraDeviceId = deviceId
    }
  }

  async changeMainCamera({ deviceId, publish = true, canvas = false, status = 1 }: ChangeCamera) {
    console.info('ChangeMainCamera', deviceId, publish, canvas, status)
    await this.changeCamera({ deviceId, publish, canvas, status }, true)
  }

  async changeViceCamera({ deviceId, publish = true, canvas = false, status = 1 }: ChangeCamera) {
    console.info('ChangeViceCamera', deviceId, publish, canvas, status)
    await this.changeCamera({ deviceId, publish, canvas, status }, false)
  }

  async changeMicrophone({ deviceId, publish = true }: ChangeMicrophone) {
    console.info('ChangeMicrophone', deviceId, publish)
    this.microphoneDeviceId = deviceId
    const action = deviceId ? '切换麦克风' : '关闭麦克风'
    try {
      if (!deviceId) {
        await this.mainClient.stopLocalAudio()
        this.localAudioTrack = null
      } else {
        await this.mainClient.startLocalAudio({ publish, option: { microphoneId: deviceId } })
        this.localAudioTrack = this.mainClient.getAudioTrack()
      }
      toast.success(`${action}成功`)
      super.emit(deviceId ? 'media-ready' : 'media-close', 'audio', { uid: this.mainUid })
    } catch (error) {
      console.info(error)
      // toast.error(`${action}失败`)
      return Promise.reject(error)
    }
  }

  async deviceOn(type: 'mainVideo' | 'viceVideo' | 'audio') {
    console.info('deviceOn', type)
    switch (type) {
      case 'mainVideo':
        await this.mainClient?.updateLocalVideo({ mute: false })
        super.emit('unmute-video', 'video', { uid: this.mainUid, videoTrack: this.localMainVideoTrack })
        break
      case 'viceVideo':
        await this.viceClient?.updateLocalVideo({ mute: false })
        super.emit('unmute-video', 'video', { uid: this.viceUid, videoTrack: this.localViceVideoTrack })
        break
      case 'audio':
        await this.mainClient?.updateLocalAudio({ mute: false })
        super.emit('unmute-audio', 'audio', { uid: this.mainUid })
        break
      default:
        break
    }
  }
  async deviceOff(type: 'mainVideo' | 'viceVideo' | 'audio') {
    console.info('deviceOff', type)
    switch (type) {
      case 'mainVideo':
        await this.mainClient?.updateLocalVideo({ mute: true })
        super.emit('mute-video', 'video', { uid: this.mainUid, videoTrack: this.localMainVideoTrack })
        break
      case 'viceVideo':
        await this.viceClient?.updateLocalVideo({ mute: true })
        super.emit('mute-video', 'video', { uid: this.viceUid, videoTrack: this.localViceVideoTrack })
        break
      case 'audio':
        await this.mainClient?.updateLocalAudio({ mute: true })
        super.emit('mute-audio', 'audio', { uid: this.mainUid })
        break
      default:
        break
    }
  }
  // 销毁
  public async destroy() {
    this.mainClient.off('*', () => {})
    this.viceClient?.off('*', () => {})
    // 离开频道。
    await this.leave()
  }
  public async leave() {
    await this.mainClient?.exitRoom()
    await this.viceClient?.exitRoom()
  }

  public static getCameras(): Promise<MediaDeviceInfo[]> {
    return TRTC.getCameraList(true) as Promise<MediaDeviceInfo[]>
  }

  public static getMicrophones(): Promise<MediaDeviceInfo[]> {
    return TRTC.getMicrophoneList(true) as Promise<MediaDeviceInfo[]>
  }

  public static getPlaybacks(): Promise<MediaDeviceInfo[]> {
    return TRTC.getSpeakerList(true) as Promise<MediaDeviceInfo[]>
  }
  public static async getSettingDevices() {
    const videoinputs = await this.getCameras()
    const audioinputs = await this.getMicrophones()
    const audiooutputs = await this.getPlaybacks()
    return [
      {
        key: 'mainCamera' as const,
        title: '主镜头',
        subTitle: '(人脸)',
        devices: videoinputs
      },
      {
        key: 'viceCamera' as const,
        title: '副镜头',
        subTitle: '(画纸)',
        devices: videoinputs
      },
      {
        key: 'microphone' as const,
        title: '麦克风',
        subTitle: '',
        devices: audioinputs
      },
      {
        key: 'playback' as const,
        title: '扬声器',
        subTitle: '',
        devices: audiooutputs
      }
    ]
  }
}
